import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { connectState, InputObservable } from '@examdojo/angular/util';
import { IconComponent } from '@examdojo/core/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as go from 'gojs';
import { BehaviorSubject, map, Observable, Subject, tap } from 'rxjs';
import { gojsOverview, GoJsOverviewPositionString, GoJsOverviewPositionToClass } from './gojs-overview.model';

const FULL_PERCENTAGE = 1;
const ZOOM_MIN_OPT = 0.1;
const ZOOM_MAX_OPT = 1.5;
const ZOOM_OPTIONS = [ZOOM_MIN_OPT, 0.15, 0.2, 0.33, 0.5, 0.75, FULL_PERCENTAGE, 1.25, ZOOM_MAX_OPT];
const ZOOM_OPTIONS_REVERSED = [...ZOOM_OPTIONS].reverse();
const ZOOM_STEP_BEYOND_MAX = 0.5;

@UntilDestroy()
@Component({
  selector: 'exam-gojs-overview',
  standalone: true,
  imports: [MatTooltipModule, IconComponent, NgClass],
  templateUrl: './gojs-overview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GojsOverviewComponent implements OnInit, OnDestroy {
  @Input({ required: true })
  @InputObservable()
  diagram!: go.Diagram;
  diagram$!: Observable<go.Diagram>;

  @Input()
  @InputObservable()
  position: GoJsOverviewPositionString = 'BottomRight';
  position$!: Observable<GoJsOverviewPositionString>;

  @ViewChild('overviewRef', { static: true }) private readonly overviewRef!: ElementRef;

  private readonly currentScale$$ = new BehaviorSubject<number>(FULL_PERCENTAGE);
  private readonly overview$$ = new BehaviorSubject<go.Overview | undefined>(undefined);
  private readonly isOverviewActive$$ = new BehaviorSubject<boolean>(false);
  private readonly viewportBoundsChanged$ = new Subject<void>();
  private readonly boundViewportBoundsChangedPusher = this.viewportBoundsChangedPusher.bind(this);

  readonly state = connectState({
    isOverviewActive: this.isOverviewActive$$,
    diagramScale: this.currentScale$$,
    diagramScaleFormatted: this.currentScale$$.pipe(map((currentScale) => `${Math.round(currentScale * 100)}%`)),
    zoomButtonsDisabled: this.currentScale$$.pipe(
      map((currentScale) => {
        return {
          out: currentScale < ZOOM_MIN_OPT,
          full: currentScale === 1,
          in: false, // unlimited zoom in
        };
      }),
    ),
    positionClass: this.position$.pipe(map((position) => GoJsOverviewPositionToClass[position])),
  });

  ngOnInit(): void {
    this.initializeOverview();
    this.diagram.addDiagramListener('ViewportBoundsChanged', this.boundViewportBoundsChangedPusher);
    this.scaleToValueOnViewportBoundsChanged().pipe(untilDestroyed(this)).subscribe();
  }

  ngOnDestroy(): void {
    this.diagram.removeDiagramListener('ViewportBoundsChanged', this.boundViewportBoundsChangedPusher);
  }

  toggleOverview(): void {
    this.isOverviewActive$$.next(!this.isOverviewActive$$.value);
  }

  fitToScreen(): void {
    this.diagram.commandHandler.zoomToFit();
  }

  zoomOut(): void {
    this.setDiagramScale(this.evalNextStepValue('decrease'));
  }

  zoomFull(): void {
    this.setDiagramScale(FULL_PERCENTAGE);
  }

  zoomIn(): void {
    this.setDiagramScale(this.evalNextStepValue('increase'));
  }

  private initializeOverview(): void {
    const overview = gojsOverview(this.diagram);
    overview.div = this.overviewRef.nativeElement;
    this.overview$$.next(overview);
    this.currentScale$$.next(this.diagram.scale);
  }

  private viewportBoundsChangedPusher() {
    this.viewportBoundsChanged$.next();
  }

  private scaleToValueOnViewportBoundsChanged() {
    return this.viewportBoundsChanged$.pipe(
      // auditTime(500),
      tap(() => {
        const diagramScale = this.diagram.scale;
        if (diagramScale !== this.currentScale$$.value) {
          this.currentScale$$.next(diagramScale);
        }
      }),
    );
  }

  private setDiagramScale(newScale: number) {
    this.diagram.scale = newScale;
    this.currentScale$$.next(newScale); // it would be set in "scaleToValueOnViewportBoundsChanged", but we want quicker update in UI
  }

  private evalNextStepValue(step: 'increase' | 'decrease'): number {
    const currentScale = this.currentScale$$.value;

    if (step === 'increase') {
      if (currentScale > ZOOM_MAX_OPT) {
        if (currentScale % ZOOM_STEP_BEYOND_MAX === 0) {
          return currentScale + ZOOM_STEP_BEYOND_MAX;
        } else {
          return Math.floor(currentScale) + ZOOM_STEP_BEYOND_MAX;
        }
      } else if (currentScale === ZOOM_MAX_OPT) {
        return currentScale + ZOOM_STEP_BEYOND_MAX;
      }
      return ZOOM_OPTIONS.find((val) => val > currentScale) ?? ZOOM_MAX_OPT;
    } else {
      if (currentScale > ZOOM_MAX_OPT) {
        if (currentScale % ZOOM_STEP_BEYOND_MAX === 0) {
          return currentScale - ZOOM_STEP_BEYOND_MAX;
        } else {
          return Math.ceil(currentScale) - ZOOM_STEP_BEYOND_MAX;
        }
      } else {
        return ZOOM_OPTIONS_REVERSED.find((val) => val < currentScale) ?? ZOOM_MIN_OPT;
      }
    }
  }
}
